import { Button, ConfigProvider, message, Modal, Table, Tooltip } from "antd";
import { TableRowSelection } from "antd/es/table/interface";
import { useCallback, useEffect, useState } from "react";
import TableCreatorName from "../TableCreatorName";
import { useTranslation } from "react-i18next";
import OrderStatusComponent from "../orderStatusComponent";
import { formatNumber } from "@/biz/tool";
import { LanguageEnum, languageLabel } from "@/pages/campaignCreate/config";
import { formatDateTime_YMD_HMS } from "@/biz/time";
import normalizePrice from "@/utils/normalizePrice";
import { useModalStore } from "@/hooks/useModalStore";
import {
  getMismatchedInfluencersListApi,
  keepInfluencersApi,
  rejectInfluencersApi,
} from "@/api/campaignList";
import { AdsOrderStatus } from "@/interface/order";

// antd Table 的行选择(checkbox)列宽（默认约 48px，精确值会随主题略有差异）
const TABLE_SELECTION_COL_WIDTH = 48;

// 为表格左右边框/边距留一点余量，避免最后一列表头被截断
const TABLE_SCROLL_X_PADDING = 24;

/** 本 Modal 在 index 的 ModalBox 中渲染，不在 App 的 ConfigProvider 内，需自包主题才能展示主题色 */
const MODAL_THEME = {
  token: { colorPrimary: "#FB6011", fontFamily: '"DM Sans"' },
  components: {
    Table: {
      headerBg: "#fff",
      headerSplitColor: "#fff",
      headerColor: "#71717A",
      footerBg: "#fff",
    },
  },
};

/** 从 tableSorter 取某列的 sortOrder 给 Table 用（与项目 collaboration 表一致：order + "end"） */
const getSortOrder = (tableSorter: any[], field: string): string | null => {
  const item = tableSorter?.find((s: any) => s.field === field);
  return item?.order ? `${item.order}end` : null;
};

/**
 * 把 tableSorter 数组转成接口常用的对象格式，例如：
 * [{ field: 'matchingScore', order: 'ascend' }, { field: 'price', order: 'descend' }]
 * => { matchingScore: 'asc', price: 'desc' }
 * 若接口要数组格式，直接用 tableSorter 即可。
 */
export const tableSorterToApiSort = (
  tableSorter: any[],
): Record<string, "asc" | "desc"> => {
  return tableSorter.reduce(
    (acc: Record<string, "asc" | "desc">, { field, order }) => {
      if (field && order) {
        acc[field] = order === "ascend" ? "asc" : "desc";
      }
      return acc;
    },
    {},
  );
};

export default function KeepOrRemoveCreatorModal() {
  const { t } = useTranslation();
  /** 多列排序状态，用于 Table 受控 + 传给接口。格式 [{ field, order }]，order 为 'ascend' | 'descend' */
  const [tableSorter, setTableSorter] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<any>({
    pageNum: 1,
    pageSize: 10,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const {
    showKeepOrRemoveCreatorModalInfo,
    setShowKeepOrRemoveCreatorModalInfo,
  } = useModalStore();
  const [total, setTotal] = useState(0);
  const [list, setList] = useState<any[]>([]);

  const getList = useCallback(async () => {
    if (
      !showKeepOrRemoveCreatorModalInfo?.campaignId ||
      !showKeepOrRemoveCreatorModalInfo.visible
    )
      return;
    setLoading(true);
    try {
      const res = await getMismatchedInfluencersListApi(
        showKeepOrRemoveCreatorModalInfo.campaignId,
        {
          paginate: {
            pageNum: pagination.pageNum,
            pageLimit: pagination.pageSize,
          },
          sorter: tableSorter,
        },
      );
      console.log("res", res);
      setList(res?.data?.list || []);
      setTotal(res?.paginate?.total || 0);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [
    pagination.pageNum,
    pagination.pageSize,
    showKeepOrRemoveCreatorModalInfo?.campaignId,
    showKeepOrRemoveCreatorModalInfo?.visible,
    tableSorter,
  ]);

  useEffect(() => {
    getList();
  }, [getList]);

  useEffect(() => {
    if (!showKeepOrRemoveCreatorModalInfo.visible) {
      setSelectedRowKeys([]);
      setTableSorter([]);
      setPagination({
        pageNum: 1,
        pageSize: 10,
      });
      setLoading(false);
      setPagination({
        pageNum: 1,
        pageSize: 10,
      });
    }
  }, [showKeepOrRemoveCreatorModalInfo.visible]);

  const columns: any[] = [
    {
      title: t("campaignDetail.creatorTableColumns.name"),
      dataIndex: "creatorName",
      width: 240,
      align: "left",
      render: (_: string, item: any) => {
        return (
          <TableCreatorName
            handleClick={() => {
              window.open(
                `/campaign/detail/${item?.campaignId}/collaboration/${item?.id}?tabValue=overview`,
                "_blank",
              );
            }}
            item={item}
          />
        );
      },
    },
    {
      title: t("campaignDetail.keepOrRemoveCreatorModal.mismatchReason", {
        defaultValue: "Mismatch reason",
      }),
      dataIndex: "mismatchReasonLabels",
      width: 190,
      align: "left",
      render: (_: string, item: any) => {
        return (
          <Tooltip title={item?.mismatchReasonLabels?.join(", ")}>
            <div className="w-[170px] line-clamp-1 text-ellipsis overflow-hidden">
              {item?.mismatchReasonLabels?.join(", ")}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: t("campaignDetail.creatorTableColumns.status"),
      dataIndex: "Status",
      width: 224,
      align: "left",
      render: (_: string, item: any) => (
        <div className="flex">
          <OrderStatusComponent data={item} />
        </div>
      ),
    },
    {
      title: t("campaignDetail.creatorTableColumns.matchingScore"),
      dataIndex: "matchingScore",
      width: 160,
      align: "left",
      sorter: { multiple: 1 },
      sortOrder: getSortOrder(tableSorter, "matchingScore"),
      render: (text: string) => (
        <>
          <div
            className={`w-fit flex gap-1 justify-center items-center px-2 py-1.5 rounded-lg ${
              +text > 80 ? "bg-[#FFF6ED]" : "bg-[#F5F5F4]"
            } font-medium`}
          >
            <div className="">{formatNumber(text)}</div>
          </div>
        </>
      ),
    },
    {
      title: t("campaignDetail.creatorTableColumns.language"),
      dataIndex: "creatorLanguage",
      width: 140,
      align: "left",
      render: (text: LanguageEnum) => <span>{languageLabel[text]}</span>,
    },
    {
      title: t("campaignDetail.creatorTableColumns.lastUpdateTime"),
      dataIndex: "lastActivityAt",
      width: 160,
      align: "left",
      sorter: { multiple: 2 },
      sortOrder: getSortOrder(tableSorter, "lastActivityAt"),
      render: (text: string) => <span>{formatDateTime_YMD_HMS(text)}</span>,
    },
    {
      title: t("campaignDetail.creatorTableColumns.bestPrice"),
      dataIndex: "price",
      width: 160,
      align: "left",
      sorter: { multiple: 3 },
      sortOrder: getSortOrder(tableSorter, "price"),
      render: (text: LanguageEnum, item: any) => (
        <div className="flex items-center gap-[10px]">
          {item?.historicalPriceRange?.[0] &&
          item?.historicalPriceRange?.[1] &&
          (item?.status === AdsOrderStatus.PendingSelection ||
            item?.status === AdsOrderStatus.QuoteRequested) ? (
            <div className="flex items-center gap-[10px]">
              {normalizePrice(item?.historicalPriceRange?.[0])} -{" "}
              {normalizePrice(item?.historicalPriceRange?.[1])}
            </div>
          ) : (
            <div className="text-[#09090B] text-sm font-medium flex items-center gap-2">
              {normalizePrice(item?.lastPrice)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t("campaignDetail.creatorTableColumns.lowestCpm"),
      dataIndex: "predictedCpmRange",
      width: 180,
      align: "left",
      sorter: { multiple: 4 },
      sortOrder: getSortOrder(tableSorter, "predictedCpmRange"),
      render: (text: LanguageEnum, record: any) => {
        return (
          <span
            style={{
              fontFamily: "Geist Mono",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {Array.isArray(record?.predictedCpmRange) ? (
              <>
                {normalizePrice(record?.predictedCpmRange?.[0])} -{" "}
                {normalizePrice(record?.predictedCpmRange?.[1])}
              </>
            ) : (
              <>{normalizePrice(record?.predictedCpm)}</>
            )}
          </span>
        );
      },
    },
    {
      title: t(
        "campaignDetail.creatorTableColumns.brandedContentPredictedViews",
      ),
      dataIndex: "predictedViewRange",
      width: 240,
      align: "left",
      sorter: true,
      sortOrder: getSortOrder(tableSorter, "predictedViewRange"),
      render: (text: LanguageEnum, record: any) => (
        <span style={{ fontFamily: "Geist Mono" }}>
          {Array.isArray(record?.predictedViewRange) ? (
            <>
              {formatNumber(record?.predictedViewRange?.[0])} -{" "}
              {formatNumber(record?.predictedViewRange?.[1])}
            </>
          ) : (
            <>{formatNumber(record?.predictedView)}</>
          )}
        </span>
      ),
    },
  ];

  // 固定宽度：确保所有列都有 width；若缺少 width，则用 minWidth 兜底（一般已全部改为 width）
  const fixedColumns = columns.map((col) => {
    const width = col?.width;
    const minWidth = col?.minWidth;
    if (typeof width !== "number" && typeof minWidth === "number") {
      return { ...col, width: minWidth };
    }
    return col;
  });

  // 根据固定后的列宽精确计算横向滚动宽度（包含选择列）
  const tableScrollX = fixedColumns.reduce((sum: number, col: any) => {
    const w = col?.width;
    return sum + (typeof w === "number" ? w : 0);
  }, 0) + TABLE_SELECTION_COL_WIDTH + TABLE_SCROLL_X_PADDING;

  const onTableChange = (pagination: any, filters: any, sorter: any) => {
    setPagination(pagination);
    let sorterResult: any[];
    if (Array.isArray(sorter) && sorter.length) {
      // 多列排序时 Ant Design 会传数组，保留所有有 order 的项
      sorterResult = sorter
        .map((el: any) =>
          el.order
            ? { field: el.field, order: el.order.replace("end", "") }
            : null,
        )
        .filter(Boolean);
    } else if (sorter?.field != null) {
      // 单列点击时传的是对象：合并到当前多列状态
      if (sorter.order) {
        const others = tableSorter.filter((s: any) => s.field !== sorter.field);
        sorterResult = [
          ...others,
          { field: sorter.field, order: sorter.order.replace("end", "") },
        ];
      } else {
        // 用户取消该列排序
        sorterResult = tableSorter.filter((s: any) => s.field !== sorter.field);
      }
    } else {
      sorterResult = [];
    }
    setTableSorter(sorterResult);
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const [rejectLoading, setRejectLoading] = useState(false);

  const handleReject = async () => {
    try {
      setRejectLoading(true);
      await rejectInfluencersApi({
        campaignId: showKeepOrRemoveCreatorModalInfo.campaignId,
        orderIds: selectedRowKeys as string[],
        reason: [],
      });
      setShowKeepOrRemoveCreatorModalInfo({
        visible: false,
        campaignId: "",
      });
      showKeepOrRemoveCreatorModalInfo?.updateFunc?.();
    } catch (error) {
      message.error((error as any)?.message || (error as any)?.error);
    } finally {
      setRejectLoading(false);
    }
  };

  const [keepLoading, setKeepLoading] = useState(false);
  const handleKeep = async () => {
    try {
      setKeepLoading(true);
      await keepInfluencersApi(showKeepOrRemoveCreatorModalInfo.campaignId, {
        orderIds: selectedRowKeys as string[],
      });
      setShowKeepOrRemoveCreatorModalInfo({
        visible: false,
        campaignId: "",
      });
      showKeepOrRemoveCreatorModalInfo?.updateFunc?.();
    } catch (error) {
      message.error((error as any)?.message || (error as any)?.error);
    } finally {
      setKeepLoading(false);
    }
  };

  return (
    <ConfigProvider theme={MODAL_THEME}>
      <Modal
        open={showKeepOrRemoveCreatorModalInfo.visible}
        title={t("campaignDetail.keepOrRemoveCreatorModal.title", {
          defaultValue:
            "These candidates no longer match your latest campaign requirements. Select the ones you want to keep.",
        })}
        width={1020}
        onCancel={() =>
          setShowKeepOrRemoveCreatorModalInfo({
            visible: false,
            campaignId: "",
          })
        }
        footer={null}
      >
        <div className="py-6 border-b border-borderColor max-h-[calc(100vh-280px)] overflow-y-auto flex flex-col min-h-0">
          <div className="border border-borderColor rounded-lg flex flex-col min-h-0 flex-1">
            {!!selectedRowKeys?.length && (
              <div className="h-[52px] flex items-center justify-between px-4 border-b border-borderColor flex-shrink-0">
                <div className="justify-start text-font&icon-primary text-sm font-normal font-['DM_Sans'] leading-snug">
                  {t("campaignDetail.keepOrRemoveCreatorModal.selectedCount", {
                    defaultValue: "{count} influencers selected",
                    count: selectedRowKeys?.length,
                  })}
                </div>
              </div>
            )}
            <Table
              rowKey="id"
              columns={fixedColumns}
              loading={loading}
              dataSource={list}
              tableLayout="fixed"
              scroll={{ x: tableScrollX }}
              onChange={onTableChange}
              pagination={{
                ...pagination,
                total: total,
                showQuickJumper: true,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              showSorterTooltip={false}
              sortDirections={["ascend", "descend"]}
              rowSelection={rowSelection}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 justify-end pt-4">
          <Button
            size="large"
            onClick={handleReject}
            loading={rejectLoading}
            disabled={!selectedRowKeys?.length}
          >
            {t("campaignDetail.keepOrRemoveCreatorModal.cancelText", {
              defaultValue: "Reject selected influencers",
            })}
          </Button>
          <Button
            size="large"
            type="primary"
            onClick={handleKeep}
            loading={keepLoading}
            disabled={!selectedRowKeys?.length}
          >
            {t("campaignDetail.keepOrRemoveCreatorModal.okText", {
              defaultValue: "Keep selected influencers",
            })}
          </Button>
        </div>
      </Modal>
    </ConfigProvider>
  );
}
